import { computed, ref, watch } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { useSegmentGroupStore } from '@dashboard/modules/segment/stores/segmentGroupStore'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import {
  buildEmailPersonalizationFieldCatalog,
  isMappedEmailPersonalizationQuestion,
  type EmailPersonalizationField
} from '../services/emailPersonalizationFieldCatalog'

export function useEmailPersonalizationAvailability() {
  const config = useConfigStore()
  const integration = useIntegrationStore()
  const groups = useSegmentGroupStore()
  const questions = useQuestionStore()
  const fieldCatalog = ref<EmailPersonalizationField[]>([])
  const catalogLoading = ref(false)
  const catalogError = ref(false)
  let request = 0
  let catalogTeam = config.currentTeamPid
  let catalogPartner = integration.integratedPartner
  const requestStatus = computed(() => {
    if (config.isError || integration.isError) {
      return 'error'
    }
    if (config.isLoading || config.configQuery.isFetching || integration.isLoading) {
      return 'loading'
    }
    return 'available'
  })
  const questionStatus = computed(() => {
    if (requestStatus.value !== 'available') {
      return requestStatus.value
    }
    return integration.integratedPartner && groups.segmentGroups.some(isMappedEmailPersonalizationQuestion)
      ? 'available'
      : 'missing'
  })
  const conditionalStatus = computed(() => {
    if (requestStatus.value !== 'available') {
      return requestStatus.value
    }
    if (catalogLoading.value) {
      return 'loading'
    }
    if (fieldCatalog.value.length) {
      return 'available'
    }
    return catalogError.value ? 'error' : 'missing'
  })
  const loadFieldCatalog = async () => {
    const currentRequest = ++request
    if (catalogTeam !== config.currentTeamPid || catalogPartner !== integration.integratedPartner) {
      fieldCatalog.value = []
    }
    catalogTeam = config.currentTeamPid
    catalogPartner = integration.integratedPartner
    catalogError.value = false
    catalogLoading.value = requestStatus.value === 'available' && !!integration.integratedPartner
    if (!catalogLoading.value) {
      return
    }
    try {
      const fields = await buildEmailPersonalizationFieldCatalog({
        integratedPartner: integration.integratedPartner,
        segmentGroups: groups.segmentGroups,
        previousFields: fieldCatalog.value,
        questionForSegmentGroupId: (id) => questions.getBySegmentGroupId(id),
        onError: () => {
          if (currentRequest === request) {
            catalogError.value = true
          }
        }
      })
      if (currentRequest === request) {
        fieldCatalog.value = fields
      }
    } catch {
      if (currentRequest === request) {
        catalogError.value = true
      }
    } finally {
      if (currentRequest === request) {
        catalogLoading.value = false
      }
    }
  }
  watch(
    [requestStatus, () => config.currentTeamPid, () => integration.integratedPartner, () => groups.segmentGroups],
    loadFieldCatalog,
    { immediate: true, deep: true }
  )
  return { fieldCatalog, questionStatus, conditionalStatus, catalogError, loadFieldCatalog }
}
